import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Sparkles, Zap, Crown, BarChart3, Lock } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../utils/apiClient';

const BillingPlans = ({ currentPlan, onPlanChange }) => {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await apiClient.get('/billing/plans');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planName) => {
    setSubscribing(planName);
    try {
      const response = await apiClient.post(`/billing/subscribe?plan_name=${planName}`);
      
      if (response.data.redirect_url) {
        // Hybrid/Managed Pricing: Redirect to Shopify Admin
        toast.info('Redirecting to Shopify to complete subscription...');
        
        // Open in top window (breaks out of iframe if embedded)
        window.open(response.data.redirect_url, '_blank');
        
        // Show message to user
        toast.success('Please complete your subscription in the new tab, then refresh this page.');
        if (onPlanChange) onPlanChange();
      } else if (response.data.confirmation_url) {
        // Programmatic billing (legacy, shouldn't happen with managed pricing)
        window.top.location.href = response.data.confirmation_url;
      } else {
        // Free plan or immediate activation
        toast.success(`Switched to ${planName} plan`);
        if (onPlanChange) onPlanChange();
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error(error.response?.data?.detail || 'Failed to subscribe to plan');
    } finally {
      setSubscribing(null);
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName) {
      case 'starter':
        return <Zap className="w-6 h-6" />;
      case 'professional':
        return <Sparkles className="w-6 h-6" />;
      case 'enterprise':
        return <Crown className="w-6 h-6" />;
      default:
        return null;
    }
  };

  const getPlanColor = (planName) => {
    switch (planName) {
      case 'starter':
        return 'border-blue-500';
      case 'professional':
        return 'border-purple-500';
      case 'enterprise':
        return 'border-amber-500';
      default:
        return 'border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-2">Choose Your Plan</h2>
        <p className="text-gray-600">Select the perfect plan for your business needs</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Object.entries(plans).map(([planKey, plan]) => {
          const isCurrentPlan = currentPlan?.toLowerCase() === planKey.toLowerCase();
          const isFree = planKey === 'free';
          
          return (
            <Card 
              key={planKey} 
              className={`relative ${getPlanColor(planKey)} ${isCurrentPlan ? 'border-2 shadow-lg' : ''}`}
            >
              {isCurrentPlan && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-500">
                  Current Plan
                </Badge>
              )}
              
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-xl capitalize">{plan.name}</CardTitle>
                  {getPlanIcon(planKey)}
                </div>
                <CardDescription>
                  <div className="space-y-1">
                    {!isFree && (
                      <div className="flex items-center gap-2">
                        <span className="text-lg text-gray-400 line-through">
                          ${plan.price * 2}
                        </span>
                        <Badge variant="secondary" className="bg-red-100 text-red-700 text-xs">
                          50% OFF
                        </Badge>
                      </div>
                    )}
                    <div className="text-3xl font-bold text-gray-900">
                      ${plan.price}
                      {!isFree && <span className="text-sm font-normal text-gray-600">/month</span>}
                    </div>
                  </div>
                </CardDescription>
              </CardHeader>

              <CardContent>
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Analytics Features Section */}
                <div className="mt-4 p-3 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-indigo-600" />
                    <p className="text-xs font-semibold text-indigo-900">Analytics Included:</p>
                  </div>
                  
                  {planKey === 'free' && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Lock className="w-3 h-3" />
                        <span>No analytics access</span>
                      </div>
                    </div>
                  )}

                  {planKey === 'starter' && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-green-700">
                        <Check className="w-3 h-3" />
                        <span className="font-medium">Total audio plays</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Lock className="w-3 h-3" />
                        <span>Completion rates locked</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Lock className="w-3 h-3" />
                        <span>Purchase tracking locked</span>
                      </div>
                    </div>
                  )}

                  {(planKey === 'professional' || planKey === 'enterprise') && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-green-700">
                        <Check className="w-3 h-3" />
                        <span className="font-medium">Total audio plays</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-green-700">
                        <Check className="w-3 h-3" />
                        <span className="font-medium">Completion rates</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-green-700">
                        <Check className="w-3 h-3" />
                        <span className="font-medium">Cart addition tracking</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-green-700">
                        <Check className="w-3 h-3" />
                        <span className="font-medium">Purchase tracking & ROI</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-green-700">
                        <Check className="w-3 h-3" />
                        <span className="font-medium">Device breakdown</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-green-700">
                        <Check className="w-3 h-3" />
                        <span className="font-medium">Product performance</span>
                      </div>
                      {planKey === 'professional' && (
                        <div className="mt-2 pt-2 border-t border-indigo-200">
                          <Badge variant="secondary" className="bg-indigo-600 text-white text-xs">
                            🎯 Best for ROI Tracking
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                  <p className="text-xs text-gray-600 font-semibold mb-1">Limits:</p>
                  <p className="text-xs text-gray-600">
                    {plan.limits.max_products === 999999 
                      ? 'Unlimited products' 
                      : `${plan.limits.max_products} products`}
                  </p>
                  <p className="text-xs text-gray-600">
                    {plan.limits.max_custom_voices === 0
                      ? 'No custom voices'
                      : `${plan.limits.max_custom_voices} custom voice${plan.limits.max_custom_voices > 1 ? 's' : ''}`}
                  </p>
                </div>
              </CardContent>

              <CardFooter>
                <Button
                  className="w-full"
                  onClick={() => handleSubscribe(planKey)}
                  disabled={isCurrentPlan || subscribing === planKey}
                  variant={isCurrentPlan ? 'outline' : 'default'}
                >
                  {subscribing === planKey ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : (
                    isFree ? 'Switch to Free' : 'Upgrade'
                  )}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> You can upgrade or downgrade your plan at any time. 
          Changes will take effect immediately for upgrades, or at the end of your billing cycle for downgrades.
        </p>
      </div>
    </div>
  );
};

export default BillingPlans;
